import { all, call, put, takeEvery, takeLatest, race, delay, cancelled } from "redux-saga/effects";
import moment from "moment-timezone";
import { PICTREE, ENDPOINT } from "../../constants";
import { getJwtToken, getDataFromSxi } from "api";
import { setPageTopLoader, setPicTreeRegularSeasonEventAreaData } from "actions";

const ALL_BOOST_EVENTS_VALUE = "__ALL__";
const LAYER_IDS = new Set(["regular_1_l1", "regular_1_l2", "regular_1_l3"]);
const ID_PATTERN = /^[A-Za-z0-9_-]+$/;

const parseRows = (response) => {
  if (Array.isArray(response)) return response;
  const body = response && Object.prototype.hasOwnProperty.call(response, "body") ? response.body : response;
  if (Array.isArray(body)) return body;
  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

const quoteIds = (ids) => ids.filter((id) => ID_PATTERN.test(id)).map((id) => `'${id}'`).join(", ");

const buildAreaQueryParts = ({ seasonId, boostEventId, areaLayerId, startUtc, endUtc }) => {
  const escapedSeasonId = ID_PATTERN.test(seasonId) ? seasonId : "";
  const escapedEventId = ID_PATTERN.test(boostEventId) ? boostEventId : "";
  const layerId = LAYER_IDS.has(areaLayerId) ? areaLayerId : "regular_1_l1";
  const seasonIds = quoteIds([escapedSeasonId]);

  const commonCtes = `
      WITH selected_event AS (
        SELECT be.id AS boost_event_id, be.game_space_id, be.start_datetime, cb.id AS capital_budget_id
        FROM boost_event be
        JOIN capital_budget cb ON cb.boost_event_id = be.id
        WHERE be.game_space_id IN (${seasonIds})
          AND be.id = '${escapedEventId}'
      ),
      raw_targets AS (
        SELECT DISTINCT se.game_space_id, btal.power_asset_id
        FROM selected_event se
        JOIN boost_target_asset_list btal ON btal.boost_event_id = se.boost_event_id
      ),
      eligible_targets AS (
        SELECT DISTINCT rt.game_space_id, rt.power_asset_id
        FROM raw_targets rt
        JOIN selected_event se ON se.game_space_id = rt.game_space_id
        JOIN budget b ON b.capital_budget_id = se.capital_budget_id
                     AND b.power_asset_id = rt.power_asset_id
        LEFT JOIN budget_event pre_budget_event
          ON pre_budget_event.budget_id = b.id
         AND pre_budget_event.occurred_datetime < se.start_datetime
        LEFT JOIN budget_event_of_reward_granted pre_reward
          ON pre_reward.id = pre_budget_event.id
        WHERE NOT EXISTS (
          SELECT 1
          FROM power_asset_event ppae
          WHERE ppae.game_space_id = rt.game_space_id
            AND ppae.power_asset_id = rt.power_asset_id
            AND ppae.type = 'PHOTOGRAPH'
            AND ppae.occurred_datetime < se.start_datetime
        )
          AND pre_reward.id IS NULL
      ),
      area_map_raw AS (
        SELECT DISTINCT
          rt.game_space_id,
          rt.power_asset_id,
          gfab.game_field_area_id AS area_id,
          COALESCE(gfad.name, gfa.area_code, gfa.id) AS area_name,
          CASE
            WHEN '${layerId}' = 'regular_1_l2' THEN parent_name.name
            WHEN '${layerId}' = 'regular_1_l3' THEN grandparent_name.name
            ELSE NULL
          END AS prefecture_name
        FROM raw_targets rt
        STRAIGHT_JOIN power_asset_game_space_area paga FORCE INDEX (idx_pagsa_game_space_power_asset_area)
          ON paga.game_space_id = rt.game_space_id
         AND paga.power_asset_id = rt.power_asset_id
        STRAIGHT_JOIN game_space gs ON gs.id = rt.game_space_id
        STRAIGHT_JOIN game_field_layer_bind gflb
          ON gflb.game_field_id = gs.game_field_id
         AND gflb.game_field_layer_id = '${layerId}'
        STRAIGHT_JOIN game_field_area_bind gfab
          ON gfab.game_field_layer_id = gflb.game_field_layer_id
         AND gfab.game_field_area_id = paga.area_id
        LEFT JOIN game_field_area gfa ON gfa.id = gfab.game_field_area_id
        LEFT JOIN game_field_area_display_name gfad
          ON gfad.area_id = gfa.id AND gfad.language_code = 'JA'
        LEFT JOIN game_field_area parent_area ON parent_area.id = gfa.superior_area_id
        LEFT JOIN game_field_area_display_name parent_name
          ON parent_name.area_id = parent_area.id AND parent_name.language_code = 'JA'
        LEFT JOIN game_field_area grandparent_area ON grandparent_area.id = parent_area.superior_area_id
        LEFT JOIN game_field_area_display_name grandparent_name
          ON grandparent_name.area_id = grandparent_area.id AND grandparent_name.language_code = 'JA'
      ),
      area_map AS (
        SELECT
          game_space_id,
          power_asset_id,
          area_id,
          MIN(area_name) AS area_name,
          MIN(prefecture_name) AS prefecture_name
        FROM area_map_raw
        GROUP BY game_space_id, power_asset_id, area_id
      ),
  `;

  const registeredQuery = {
    query: `${commonCtes}
      registered AS (
        SELECT am.area_id, MIN(am.area_name) AS area_name, MIN(am.prefecture_name) AS prefecture_name,
               COUNT(DISTINCT et.power_asset_id) AS registered_count
        FROM eligible_targets et
        JOIN area_map am ON am.game_space_id = et.game_space_id AND am.power_asset_id = et.power_asset_id
        GROUP BY am.area_id
      )
      SELECT area_id, area_name, prefecture_name, registered_count
      FROM registered
      ORDER BY registered_count DESC, area_name ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const approvalQuery = {
    query: `${commonCtes}
      approved_assets AS (
        SELECT DISTINCT rt.game_space_id, rr.power_asset_id, rr.player_id
        FROM raw_targets rt
        JOIN review_request rr
          ON rr.game_space_id = rt.game_space_id AND rr.power_asset_id = rt.power_asset_id
        JOIN review_request_event rre
          ON rre.game_space_id = rt.game_space_id AND rre.review_request_id = rr.id
         AND rre.type = 'APPROVED'
         AND rre.occurred_datetime >= '${startUtc}'
         AND rre.occurred_datetime < '${endUtc}'
        JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
      ),
      approved_by_area AS (
        SELECT am.area_id, COUNT(DISTINCT aa.power_asset_id) AS reviewok_count,
               COUNT(DISTINCT aa.player_id) AS participants_count
        FROM area_map am
        JOIN approved_assets aa
          ON aa.game_space_id = am.game_space_id AND aa.power_asset_id = am.power_asset_id
        GROUP BY am.area_id
      ),
      global_participants AS (
        SELECT COUNT(DISTINCT player_id) AS total_participants FROM approved_assets
      )
      SELECT a.area_id, a.reviewok_count, a.participants_count, gp.total_participants
      FROM approved_by_area a
      CROSS JOIN global_participants gp
      ORDER BY a.reviewok_count DESC, a.area_id ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  return { registeredQuery, approvalQuery };
};

const callWithTimeoutAndRetry = function* (fn, args, timeoutMs = 28000) {
  try {
    const result = yield race({ response: call(fn, ...args), timeout: delay(timeoutMs) });
    if (result.timeout) throw new Error(`timeout after ${timeoutMs}ms`);
    return result.response;
  } finally {
    if (yield cancelled()) return null;
  }
};

function* handleEventAreaData(action) {
  const payload = action?.payload || {};
  const seasonId = payload.selectedSeasonId;
  const boostEventId = payload.selectedBoostEventId;
  const areaLayerId = payload.selectedAreaLayerId;
  const [startDate, endDate] = payload.selectedDateRange || [];
  const requestKey = [seasonId, boostEventId, areaLayerId, startDate, endDate].join("|");

  if (!ID_PATTERN.test(seasonId || "") || !ID_PATTERN.test(boostEventId || "")
    || boostEventId === ALL_BOOST_EVENTS_VALUE || !LAYER_IDS.has(areaLayerId) || !startDate || !endDate) {
    yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: null, areaTable: [], areaLoading: false, areaError: null, areaRequestKey: requestKey }));
    return;
  }

  yield put(setPageTopLoader(true));
  yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: null, areaTable: [], areaLoading: true, areaError: null, areaRequestKey: requestKey }));
  try {
    const jwtToken = yield call(getJwtToken);
    const startUtc = moment.tz(startDate, "Asia/Tokyo").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment.tz(endDate, "Asia/Tokyo").add(1, "day").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const { registeredQuery, approvalQuery } = buildAreaQueryParts({ seasonId, boostEventId, areaLayerId, startUtc, endUtc });
    const { registeredResult, approvalResult } = yield all({
      registeredResult: call(callWithTimeoutAndRetry, getDataFromSxi, [registeredQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken]),
      approvalResult: call(callWithTimeoutAndRetry, getDataFromSxi, [approvalQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken]),
    });
    const registeredRows = parseRows(registeredResult);
    const approvalRows = parseRows(approvalResult);
    const approvalByArea = new Map(approvalRows.map((row) => [row.area_id, row]));
    const totalParticipants = Number(approvalRows[0]?.total_participants) || 0;
    const normalized = registeredRows.map((row, index) => {
      const approval = approvalByArea.get(row.area_id) || {};
      const registeredCount = Number(row.registered_count) || 0;
      const reviewOk = Number(approval.reviewok_count) || 0;
      return {
      areaId: row.area_id,
      name: row.area_name || row.area_id,
      prefectureName: row.prefecture_name || "",
      count: registeredCount,
      unshotCount: registeredCount - reviewOk,
      reviewOk,
      achievedPercent: registeredCount ? Number(((reviewOk / registeredCount) * 100).toFixed(2)) : 0,
      participants: Number(approval.participants_count) || 0,
      index: index + 1,
      totalParticipants,
      };
    });
    const summary = normalized.reduce((resultRow, row) => ({
      registeredAreaCount: resultRow.registeredAreaCount + 1,
      unshotAreaCount: resultRow.unshotAreaCount + (row.reviewOk === 0 ? 1 : 0),
      totalRegistered: resultRow.totalRegistered + row.count,
      totalReviewOk: resultRow.totalReviewOk + row.reviewOk,
      totalParticipants: row.totalParticipants,
    }), { registeredAreaCount: 0, unshotAreaCount: 0, totalRegistered: 0, totalReviewOk: 0, totalParticipants: 0 });
    summary.totalAchievedPercent = summary.totalRegistered ? Number(((summary.totalReviewOk / summary.totalRegistered) * 100).toFixed(2)) : 0;
    const table = normalized.map((row) => [
      row.index,
      row.name,
      ...(areaLayerId === "regular_1_l1" ? [] : [row.prefectureName]),
      row.count,
      row.unshotCount,
      row.reviewOk,
      row.achievedPercent,
      row.participants,
    ]);
    yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: summary, areaTable: table, areaLoading: false, areaError: null, areaRequestKey: requestKey }));
  } catch (error) {
    yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: null, areaTable: [], areaLoading: false, areaError: "エリア別データの取得に失敗しました。", areaRequestKey: requestKey }));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

function* clearEventAreaData() {
  yield put(setPicTreeRegularSeasonEventAreaData({
    areaSummary: null,
    areaTable: [],
    areaLoading: false,
    areaError: null,
    areaRequestKey: null,
  }));
}

export default function* pictreeRegularSeasonEventAreaSaga() {
  yield takeEvery(PICTREE.GET_REGULAR_SEASON_EVENT_BOOTSTRAP_DATA, clearEventAreaData);
  yield takeLatest(PICTREE.GET_REGULAR_SEASON_EVENT_AREA_DATA, handleEventAreaData);
}
